import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Spinner,
  Text,
  Box,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import {
  createPersona,
  latestPersonaStatus,
  patchPersona,
  personaDeatil,
} from "../../api";

export default function PersonaDetailModal({ isOpen, onClose, creator, data }) {
  const [loading, setLoading] = useState(false);
  const [persona, setPersona] = useState(null);
  const [error, setError] = useState(null);
  const [statusText, setStatusText] = useState(null);

  useEffect(() => {
    if (!isOpen || !creator) return;
    loadPersona();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, creator]);

  const loadPersona = async () => {
    try {
      setLoading(true);
      setError(null);
      setPersona(null);
      setStatusText(null);

      let personaId = creator.personaId;

      // 1. personaId 없으면 최신 작업을 먼저 확인하고, 없을 때만 생성한다.
      if (personaId == null) {
        const latest = await latestPersonaStatus({
          platform: "instagram",
          profileId: creator.creatorProfileId,
        });

        personaId = latest?.data?.personaId ?? null;

        if (personaId == null) {
          const created = await createPersona({
            platform: "instagram",
            profileId: creator.creatorProfileId,
          });

          personaId = created?.data?.personaId ?? created?.personaId;
        }

        if (!personaId) {
          throw new Error("personaId 생성 실패");
        }

        await patchPersona({
          campaignId: data,
          profileId: creator.creatorProfileId,
          personaId,
        });
      }

      // 2. DB 상세 조회. 외부 API 상태 확인은 서버 스케줄러가 처리한다.
      const res = await personaDeatil({
        platform: "instagram",
        profileId: creator.creatorProfileId,
        personaId,
      });

      const status = res?.data?.status;
      if (status !== "COMPLETED" && status !== "completed") {
        setStatusText("페르소나 생성 중입니다...");
        return;
      }

      setPersona(res);
    } catch (e) {
      console.error(e);
      setError("페르소나 정보를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const output = persona?.data?.personaData?.outputs?.[0];
  const profile = output?.persona_profile;
  const interest = output?.persona_interest;
  const detail = output?.persona_detail;
  const keywords = interest?.keywords;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent maxH="80vh">
        <ModalHeader>페르소나 상세</ModalHeader>
        <ModalCloseButton />

        <ModalBody overflowY="auto" pb="24px">
          {loading && (
            <Box textAlign="center" py="40px">
              <Spinner size="lg" />
              <Text mt="12px">
                {statusText ||
                  `페르소나 ${creator.personaId ? "조회 중" : "생성 중"}...`}
              </Text>
            </Box>
          )}

          {error && <Text color="red.500">{error}</Text>}

          {statusText && !loading && !error && (
            <Box textAlign="center" py="40px">
              <Spinner size="lg" />
              <Text mt="12px">{statusText}</Text>
            </Box>
          )}

          {profile && !loading && (
            <Box display="flex" flexDir="column" gap="20px">
              {/* 1. 프로필 카드 */}
              <Box border="1px solid #E2E8F0" borderRadius="12px" p="16px">
                <Box display="flex" gap="16px" alignItems="center">
                  <img
                    src={profile.profile_image_url}
                    width={64}
                    height={64}
                    style={{ borderRadius: "50%" }}
                    alt=""
                  />

                  <Box>
                    <Text fontWeight="700">Instagram Persona</Text>
                    <Text fontSize="14px">나이: {profile.age}</Text>
                    <Text fontSize="14px">성별: {profile.gender}</Text>
                    <Text fontSize="14px">
                      팔로워: {profile.followers.toLocaleString()}
                    </Text>
                  </Box>
                </Box>
              </Box>

              {/* 2. 핵심 지표 */}
              <Box
                display="grid"
                gridTemplateColumns="repeat(3, 1fr)"
                gap="12px"
              >
                <StatBox label="게시물 수" value={profile.post_counts} />
                <StatBox label="평균 조회수" value={profile.avg_views} />
                <StatBox label="참여율" value={`${profile.engagement_rate}%`} />
              </Box>

              {/* 3. 페르소나 태그 */}
              <Section title="페르소나 태그">
                {profile.persona_tags.map((t) => (
                  <Tag key={t} text={t} />
                ))}
                {profile.metric_tags.map((t) => (
                  <Tag key={t} text={t} color="#E6FFFA" />
                ))}
              </Section>

              {/* 4. 주요 관심사 요약 */}
              <Box>
                <Text fontWeight="700" mb="6px">
                  주요 관심사
                </Text>
                <Text fontSize="14px">
                  {interest.profile_interest.join(" · ")}
                </Text>
              </Box>

              {/* 5. 키워드 분석 */}
              <KeywordSection title="무드 키워드" list={keywords.mood} />
              <KeywordSection title="오브젝트 키워드" list={keywords.objects} />
              <KeywordSection title="관심사 키워드" list={keywords.interests} />

              {/* 6. 인사이트 */}
              <InsightSection title="무드 분석" text={interest.mood_insight} />
              <InsightSection
                title="오브젝트 분석"
                text={interest.objects_insight}
              />
              <InsightSection
                title="관심사 분석"
                text={interest.interests_insight}
              />
              <InsightSection title="종합 분석" text={detail.insight} />

              {/* 7. 최근 게시물 */}
              <Box>
                <Text fontWeight="700" mb="8px">
                  최근 게시물
                </Text>
                <Box display="flex" gap="8px" overflowX="auto">
                  {profile.latest_post_image_urls.map((url) => (
                    <img
                      key={url}
                      src={url}
                      width={90}
                      height={90}
                      style={{ borderRadius: "8px", objectFit: "cover" }}
                      alt=""
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

/* ===== UI helpers ===== */

function Section({ title, children }) {
  return (
    <Box>
      <Text fontWeight="700" mb="8px">
        {title}
      </Text>
      <Box display="flex" gap="6px" flexWrap="wrap">
        {children}
      </Box>
    </Box>
  );
}

function KeywordSection({ title, list }) {
  if (!list?.length) return null;

  return (
    <Box>
      <Text fontWeight="700" mb="6px">
        {title}
      </Text>
      <Box display="flex" gap="6px" flexWrap="wrap">
        {list.map((k) => (
          <Tag key={k} text={k} color="#EDF2F7" />
        ))}
      </Box>
    </Box>
  );
}

function StatBox({ label, value }) {
  return (
    <Box
      border="1px solid #E2E8F0"
      borderRadius="10px"
      p="12px"
      textAlign="center"
    >
      <Text fontSize="12px" color="gray.500">
        {label}
      </Text>
      <Text fontWeight="700">{value}</Text>
    </Box>
  );
}

function Tag({ text, color = "#EDF2F7" }) {
  return (
    <Box px="8px" py="2px" bg={color} borderRadius="12px" fontSize="12px">
      {text}
    </Box>
  );
}

function InsightSection({ title, text }) {
  if (!text) return null;

  return (
    <Box>
      <Text fontWeight="700" mb="4px">
        {title}
      </Text>
      <MarkdownText text={text} />
    </Box>
  );
}

function MarkdownText({ text }) {
  const normalized = String(text ?? "")
    .replace(/\r\n/g, "\n")
    .replace(/(#{2,3}\s)/g, "\n$1")
    .replace(/\s-\s\*\*/g, "\n- **")
    .trim();

  if (!normalized) return null;

  return (
    <Box display="flex" flexDir="column" gap="8px">
      {normalized.split("\n").map((raw, idx) => {
        const line = raw.trim();
        if (!line) return null;

        if (line.startsWith("## ")) {
          return (
            <Text key={idx} fontSize="16px" fontWeight="800" color="#172940" mt={idx === 0 ? 0 : 2}>
              {renderInlineMarkdown(line.replace(/^##\s+/, ""))}
            </Text>
          );
        }

        if (line.startsWith("### ")) {
          return (
            <Text key={idx} fontSize="15px" fontWeight="700" color="#172940" mt={idx === 0 ? 0 : 1}>
              {renderInlineMarkdown(line.replace(/^###\s+/, ""))}
            </Text>
          );
        }

        if (line.startsWith("- ")) {
          return (
            <Box key={idx} display="flex" alignItems="flex-start" gap="8px" pl="4px">
              <Text as="span" color="gray.500" lineHeight="1.7">
                •
              </Text>
              <Text as="span" fontSize="14px" color="gray.700" lineHeight="1.7">
                {renderInlineMarkdown(line.replace(/^-\s+/, ""))}
              </Text>
            </Box>
          );
        }

        return (
          <Text key={idx} fontSize="14px" color="gray.700" lineHeight="1.7">
            {renderInlineMarkdown(line)}
          </Text>
        );
      })}
    </Box>
  );
}

function renderInlineMarkdown(text) {
  const parts = String(text ?? "").split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, idx) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <Text as="strong" key={idx} fontWeight="800">
          {part.slice(2, -2)}
        </Text>
      );
    }
    return part;
  });
}
